import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ENVIRONMENT_LOGIN_TOKEN, ENVIRONMENT_WEBSITE_TOKEN } from '@campus/environment';
import { CampaignInterface, RegistrationCredentials } from '@campus/public-portal';
import '@diekeure/dcr-components/dcr-animated-kai.js';
import '@diekeure/dcr-components/dcr-dialog.js';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { RedirectService } from '../../services/redirect.service';
import { RegisterTeacherInterface } from './components/register-teacher-form/register-teacher-form.component';
import { RegisterPageContainerViewModel } from './register-page-container.viewmodel';

type RegistrationRole = 'teacher' | 'student';

@Component({
  standalone: false,
  selector: 'polpo-register-page-container',
  templateUrl: './register-page-container.component.html',
  providers: [RegisterPageContainerViewModel],
})
export class RegisterPageContainerComponent implements OnInit {
  private vm = inject(RegisterPageContainerViewModel);
  private environmentWebsite = inject(ENVIRONMENT_WEBSITE_TOKEN);
  private environmentLogin = inject(ENVIRONMENT_LOGIN_TOKEN);
  private route = inject(ActivatedRoute);
  private redirectService = inject(RedirectService);

  public registrationError$ = this.vm.registrationError$;
  public role$: Observable<RegistrationRole>;
  public campaign$: Observable<CampaignInterface>;
  public isMaintenanceMode$: Observable<boolean>;
  public statusPageUrl: string;

  private welcomeDialogSubject = new BehaviorSubject<{
    headline: string;
    confirmLabel: string;
    confirmType: string;
    existingEmail?: string;
    redirectUrl: string;
    campaign?: string;
  } | null>(null);
  public welcomeDialog$ = this.welcomeDialogSubject.asObservable();

  ngOnInit(): void {
    this.isMaintenanceMode$ = this.vm.isMaintenanceMode$;
    this.statusPageUrl = this.environmentWebsite.statusPageUrl;

    this.role$ = this.route.queryParams.pipe(
      map((params) => (params.role as RegistrationRole) || 'student'),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.campaign$ = this.route.queryParams.pipe(
      filter((params) => params.campaign),
      map((params) => params.campaign),
      switchMap((campaignKey) =>
        this.vm.getCampaign(campaignKey).pipe(catchError(() => from([{} as CampaignInterface])))
      ),

      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  public register(data: RegistrationCredentials) {
    this.vm.register(data);
  }

  public registerTeacher(data: RegisterTeacherInterface, campaign: string) {
    this.vm.registerTeacher(data, campaign).subscribe((loginLink) => {
      const emailExists = loginLink === 'email_exists';
      const existingEmail = emailExists ? data.email : undefined;

      const welcomeDialogData = emailExists
        ? {
            headline: `Welkom terug op Polpo, ${data.firstName}!`,
            confirmLabel: 'Aanmelden',
            confirmType: 'confirm-cancel',
            existingEmail,
            redirectUrl: this.environmentLogin.url,
            campaign,
          }
        : {
            headline: `Welkom op Polpo, ${data.firstName}!`,
            confirmLabel: 'Naar het platform',
            confirmType: 'confirm',
            existingEmail,
            redirectUrl: loginLink,
            campaign,
          };

      return this.welcomeDialogSubject.next(welcomeDialogData);
    });
  }

  public onWelcomeConfirm() {
    const dialogData = this.welcomeDialogSubject.value;
    this.welcomeDialogSubject.next(null);
    if (dialogData) {
      this.redirectService.redirect(dialogData.redirectUrl);
    }
  }

  public onWelcomeCancel() {
    this.welcomeDialogSubject.next(null);
  }
}
